import { useQuery } from '@apollo/client'
import * as d3 from 'd3-hierarchy'
import { useMemo } from 'react'

import {
  IPrepItemCategoryType,
  IPrepItemSettingsType,
  getPrepItemCategory,
  getPrepItemSettings,
} from '../gql'

const usePrepItemCategoryDataSource = () => {
  const {
    data: prepItemCategoryData,
    loading: prepItemCategoryLoading,
    refetch,
  } = useQuery<IPrepItemCategoryType>(getPrepItemCategory)

  const {
    data: settingsData,
    loading: settingsLoading,
    refetch: refetchSettings,
  } = useQuery<IPrepItemSettingsType>(getPrepItemSettings)

  const dataSource = useMemo(() => {
    if (!prepItemCategoryData?.getPrepItemCategory) return []

    const nodes = prepItemCategoryData.getPrepItemCategory.nodes

    // Add root node and prepare data for d3.stratify
    const hierarchyData = [
      { id: 'root', parentId: null, name: 'Root' },
      ...nodes.map((node) => ({
        id: node.prepItemIdCode,
        parentId:
          node.parentPrepCategoryIdCode === 'root'
            ? 'root'
            : node.parentPrepCategoryIdCode,
        name: node.prepItemName,
      })),
    ]

    // Create hierarchy using d3.stratify
    const root = d3.stratify<typeof hierarchyData[0]>()(hierarchyData)

    // Create a map for quick category name lookup
    const categoryMap = new Map<string, string>()
    root.descendants().forEach((node) => {
      if (node.data.id !== 'root') {
        categoryMap.set(node.data.id, node.data.name)
      }
    })

    // Get all leaf nodes (prep items that have no children)
    const leafNodes = root
      .leaves()
      .filter((leaf) => leaf.data.id !== 'root')
      .map((leaf) => {
        // Find rest days for this prep item
        const itemSettings = settingsData?.getPrepItemSettings.nodes.find(
          (setting) => setting.prepItemIdCode === leaf.data.id,
        )

        // Determine OnHand Unit display
        let onHandUnit = 'Standard Unit'
        if (itemSettings?.displayOnHandBatchType) {
          if (itemSettings.displayOnHandBatchType === 'Standard Unit') {
            onHandUnit = 'Standard Unit'
          } else {
            const sourceType = itemSettings.displayOnHandSourceType || ''
            onHandUnit = `${itemSettings.displayOnHandBatchType} (${sourceType})`
          }
        }

        return {
          key: leaf.data.id,
          prepItem: leaf.data.name,
          // Get parent's name from the hierarchy
          category: leaf.parent
            ? categoryMap.get(leaf.parent.data.id) || leaf.parent.data.id
            : '',
          restDays: itemSettings?.restDays ?? 0,
          onHandUnit: onHandUnit,
        }
      })

    return leafNodes
  }, [prepItemCategoryData, settingsData])

  const refetchAll = async () => {
    await Promise.all([refetch(), refetchSettings()])
  }

  return {
    loading: prepItemCategoryLoading || settingsLoading,
    dataSource,
    refetch: refetchAll,
  }
}

export default usePrepItemCategoryDataSource
